"use client"

import * as React from "react"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
} from "../../components/drawer"
import { cn } from "../../lib/utils"
import { cva, type VariantProps } from "class-variance-authority"
import { UIBox } from "../layout/ui-box"

// ============================================================================
// UIDrawer Types
// ============================================================================

export interface UIDrawerProps extends VariantProps<typeof drawerVariants> {
  /**
   * Drawer 열림/닫힘 상태
   */
  open?: boolean
  /**
   * 상태 변경 콜백
   */
  onOpenChange?: (open: boolean) => void
  /**
   * Drawer 방향 (모바일 네이티브 스타일)
   */
  direction?: 'top' | 'bottom' | 'left' | 'right'
  /**
   * Drawer 크기
   */
  size?: 'sm' | 'default' | 'lg' | 'xl' | 'full'
  /**
   * 모달 동작 여부
   */
  modal?: boolean
  /**
   * 외부 클릭으로 닫기 비활성화
   */
  dismissible?: boolean
  /**
   * Drawer 트리거 요소
   */
  trigger?: React.ReactNode
  /**
   * Drawer 내용
   */
  children: React.ReactNode
  /**
   * 제목 (접근성용)
   */
  title?: string
  /**
   * 설명 (접근성용)
   */
  description?: string
  /**
   * 헤더 표시 여부
   */
  showHeader?: boolean
  /**
   * 푸터 표시 여부
   */
  showFooter?: boolean
  /**
   * 헤더 내용
   */
  headerContent?: React.ReactNode
  /**
   * 푸터 내용
   */
  footerContent?: React.ReactNode
  /**
   * 컨테이너 클래스명
   */
  className?: string
  /**
   * 콘텐츠 클래스명
   */
  contentClassName?: string
}

const drawerVariants = cva(
  "w-full max-w-full min-w-0 flex-shrink-0", // Width Stability 패턴
  {
    variants: {
      variant: {
        default: "bg-background border-border",
        elevated: "bg-card border-border shadow-2xl",
        overlay: "bg-background/95 border-border/50",
        minimal: "bg-background border-0",
      },
      size: {
        sm: "",
        default: "",
        lg: "",
        xl: "",
        full: "",
      },
    },
    compoundVariants: [
      // Bottom/Top drawer sizes
      {
        size: "sm",
        className: "data-[vaul-drawer-direction=bottom]:h-[30vh] data-[vaul-drawer-direction=top]:h-[30vh]",
      },
      {
        size: "default",
        className: "data-[vaul-drawer-direction=bottom]:h-[50vh] data-[vaul-drawer-direction=top]:h-[50vh]",
      },
      {
        size: "lg",
        className: "data-[vaul-drawer-direction=bottom]:h-[70vh] data-[vaul-drawer-direction=top]:h-[70vh]",
      },
      {
        size: "xl",
        className: "data-[vaul-drawer-direction=bottom]:h-[85vh] data-[vaul-drawer-direction=top]:h-[85vh]",
      },
      {
        size: "full",
        className: "data-[vaul-drawer-direction=bottom]:h-[95vh] data-[vaul-drawer-direction=top]:h-[95vh]",
      },
      // Left/Right drawer sizes
      {
        size: "sm",
        className: "data-[vaul-drawer-direction=left]:w-80 data-[vaul-drawer-direction=right]:w-80",
      },
      {
        size: "default",
        className: "data-[vaul-drawer-direction=left]:w-96 data-[vaul-drawer-direction=right]:w-96",
      },
      {
        size: "lg",
        className: "data-[vaul-drawer-direction=left]:w-[32rem] data-[vaul-drawer-direction=right]:w-[32rem]",
      },
      {
        size: "xl",
        className: "data-[vaul-drawer-direction=left]:w-[40rem] data-[vaul-drawer-direction=right]:w-[40rem]",
      },
      {
        size: "full",
        className: "data-[vaul-drawer-direction=left]:w-[90vw] data-[vaul-drawer-direction=right]:w-[90vw]",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

// ============================================================================
// UIDrawer Component
// ============================================================================

export const UIDrawer = React.forwardRef<
  HTMLDivElement,
  UIDrawerProps
>(({
  open,
  onOpenChange,
  direction = 'bottom',
  size,
  variant,
  modal = true,
  dismissible = true,
  trigger,
  children,
  title,
  description,
  showHeader = true,
  showFooter = false,
  headerContent,
  footerContent,
  className,
  contentClassName,
  ...props
}, ref) => {
  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      direction={direction}
      modal={modal}
      dismissible={dismissible}
      {...props}
    >
      {trigger && <DrawerTrigger asChild>{trigger}</DrawerTrigger>}

      <DrawerContent
        className={cn(
          drawerVariants({ variant, size }),
          className,
          contentClassName
        )}
      >
        {/* 헤더 영역 */}
        {showHeader && (title || description || headerContent) && (
          <DrawerHeader>
            {title && <DrawerTitle>{title}</DrawerTitle>}
            {description && <DrawerDescription>{description}</DrawerDescription>}
            {headerContent}
          </DrawerHeader>
        )}

        {/* 메인 콘텐츠 */}
        <UIBox className="flex-1 overflow-auto p-4">
          {children}
        </UIBox>

        {/* 푸터 영역 */}
        {showFooter && footerContent && (
          <DrawerFooter>
            {footerContent}
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  )
})

UIDrawer.displayName = "UIDrawer"

// ============================================================================
// Preset Components
// ============================================================================

export interface UIDrawerSheetProps extends Omit<UIDrawerProps, 'direction' | 'size'> {
  /**
   * 사이드 위치
   */
  side?: 'left' | 'right'
  /**
   * 시트 크기
   */
  sheetSize?: 'sm' | 'default' | 'lg'
}

/**
 * 사이드바 스타일 drawer (좌/우)
 */
export const UIDrawerSheet = React.forwardRef<
  React.ComponentRef<typeof Drawer>,
  UIDrawerSheetProps
>(({
  side = 'right',
  sheetSize = 'default',
  ...props
}, ref) => {
  return (
    <UIDrawer
      ref={ref}
      direction={side}
      size={sheetSize}
      variant="elevated"
      {...props}
    />
  )
})

UIDrawerSheet.displayName = "UIDrawerSheet"

export interface UIDrawerDialogProps extends Omit<UIDrawerProps, 'direction'> {
  /**
   * 다이얼로그 위치
   */
  position?: 'top' | 'bottom'
}

/**
 * 다이얼로그 스타일 drawer (상/하)
 */
export const UIDrawerDialog = React.forwardRef<
  React.ComponentRef<typeof Drawer>,
  UIDrawerDialogProps
>(({
  position = 'bottom',
  size = 'default',
  showHeader = true,
  showFooter = true,
  ...props
}, ref) => {
  return (
    <UIDrawer
      ref={ref}
      direction={position}
      size={size}
      variant="elevated"
      showHeader={showHeader}
      showFooter={showFooter}
      {...props}
    />
  )
})

UIDrawerDialog.displayName = "UIDrawerDialog"

// ============================================================================
// DEPRECATED: UIDrawerNotification
// ============================================================================
//
// ⚠️ UIDrawerNotification은 더 이상 사용되지 않습니다.
//
// 대신 다음을 사용하세요:
// - UISonner: 표준 toast notification (추천)
// - UIAlert: 페이지 내 정적 알림
// - UIDialog: 중요한 확인 메시지
//
// 예시:
// import { UISonner } from '../feedback/ui-sonner'
// UISonner.success('작업이 완료되었습니다!')
// UISonner.error('오류가 발생했습니다.')
//
// ============================================================================

// ============================================================================
// Individual Component Exports
// ============================================================================

export const UIDrawerTrigger = DrawerTrigger
export const UIDrawerContent = DrawerContent
export const UIDrawerHeader = DrawerHeader
export const UIDrawerFooter = DrawerFooter
export const UIDrawerTitle = DrawerTitle
export const UIDrawerDescription = DrawerDescription
export const UIDrawerClose = DrawerClose
export const UIDrawerOverlay = DrawerOverlay
export const UIDrawerPortal = DrawerPortal

// ============================================================================
// Type Re-exports
// ============================================================================
// Note: All types are automatically exported via their interface declarations